import type { AvailableLocales } from '@eliadmin/core/src/stores/locale-store';

export function isNullish(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

// deepGet function
export function deepGet(obj: any, path: string | string[], defaultValue?: any): any {
  const pathArr = Array.isArray(path) ? path : path.split('.');
  return pathArr.reduce((acc, key) => (acc && acc[key] ? acc[key] : defaultValue), obj);
}

export function deepResolveDefaults<T extends object>(obj1: T, obj2: T): T {
  // deeply traverse object always pick the value from obj1 if it exists
  // otherwise pick the value from obj2

  // if both values are objects, recursively call deepResolveDefaults
  // otherwise, return the value from obj1 or obj2

  const result = { ...obj1 };

  for (const key in obj2) {
    if (key in obj1 && !isNullish(obj1[key])) {
      if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object') {
        result[key] = deepResolveDefaults<any>(obj1[key], obj2[key]);
      } else {
        result[key] = obj1[key];
      }
    } else {
      result[key] = obj2[key];
    }
  }

  return result;
}

/**
 * Removes all tags from an HTML string
 */
export function getTextFromHTML(html: string): string {
  return html?.replace(/<[^>]+>/g, '') ?? '';
}

export function ensureIsFromArr<T>(value: T, arr: T[], fallback: T): T {
  return arr.includes(value) ? value : fallback;
}

export function youtubeEmbedToNotEmbed(url: string): string {
  const replaced = url.replace('embed/', 'watch?v=');
  const hasProtocol = /^https?:\/\//i.test(replaced);
  return hasProtocol ? replaced : 'https://' + replaced;
}

export const isServer = typeof window === 'undefined';

/**
 * returns date in format: yyyy-mm-dd
 */
export function getDateString(date: Date) {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function getDefaultUrlForTosOrGdprLocalized(
  url: string,
  locale: AvailableLocales,
  type: 'tos' | 'gdpr',
) {
  if (url != '/') return url;

  if (locale === 'en') {
    switch (type) {
      case 'tos':
        return '/general-business-terms';
      case 'gdpr':
        return '/protection-of-personal-data';
    }
  }
  if (locale === 'sk') {
    switch (type) {
      case 'tos':
        return '/vseobecne-obchodne-podmienky';
      case 'gdpr':
        return '/ochrana-osobnych-udajov';
    }
  }
  if (locale === 'cs') {
    switch (type) {
      case 'tos':
        return '/vseobecne-obchodni-podminky';
      case 'gdpr':
        return '/ochrana-osobnich-udaju';
    }
  }
  if (locale === 'de') {
    switch (type) {
      case 'tos':
        return '/allgemeine-geschaftsbedingungen';
      case 'gdpr':
        return '/datenschutz';
    }
  }
  if (locale === 'hu') {
    switch (type) {
      case 'tos':
        return '/altalanos-szerzodesi-feltetelek';
      case 'gdpr':
        return '/adatvedelmi-nyilatkozat';
    }
  }
  if (locale === 'pl') {
    switch (type) {
      case 'tos':
        return '/regulamin';
      case 'gdpr':
        return '/polityka-prywatnosci';
    }
  }
  if (locale === 'it') {
    switch (type) {
      case 'tos':
        return '/termini-commerciali-generali';
      case 'gdpr':
        return '/protezione-dei-dati-personali';
    }
  }
  if (locale === 'si') {
    switch (type) {
      case 'tos':
        return '/splosni-pogoji';
      case 'gdpr':
        return '/politika-zasebnosti';
    }
  }
  if (locale === 'hr') {
    switch (type) {
      case 'tos':
        return '/opci-uvjeti';
      case 'gdpr':
        return '/politika-privatnosti';
    }
  }
  if (locale === 'fr') {
    switch (type) {
      case 'tos':
        return '/conditions-generales-daffaires';
      case 'gdpr':
        return '/protection-des-donnees-personnelles';
    }
  }
  if (locale === 'ru') {
    switch (type) {
      case 'tos':
        return '/usloviya-ispolzovaniya';
      case 'gdpr':
        return '/politika-konfidentsialnosti';
    }
  }
  if (locale === 'ro') {
    switch (type) {
      case 'tos':
        return '/termeni-si-conditii';
      case 'gdpr':
        return '/politica-de-confidentialitate';
    }
  }
  if (locale === 'es') {
    switch (type) {
      case 'tos':
        return '/terminos-comerciales-generales';
      case 'gdpr':
        return '/proteccion-de-datos-personales';
    }
  }
  if (locale === 'fi') {
    switch (type) {
      case 'tos':
        return '/kayttoehdot';
      case 'gdpr':
        return '/tietosuojaseloste';
    }
  }

  return '/';
}

export const $do = <T>(fn: () => T): T => fn();
